import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Stack,
  Markdown
} from "@altimateai/lego";
import { CopyButton } from "@/components/copyButton";
import { useQuery } from '@tanstack/react-query';
import { useState } from "react";
import { GET_BI_DASHBOARD_BY_ID } from "@/pages/query/constants";
import { getBiDashboardById } from "@/helpers/apis";
import { RelativeComponentLoader } from "@/components/loader";

interface InstrumentationDialogProps {
  children: React.ReactNode;
  instrumentationSnippet?: string | null;
  dashboardName?: string;
  dashboardRk?: string;
}

const InstrumentationDialog = ({
  children,
  instrumentationSnippet,
  dashboardName,
  dashboardRk,
}: InstrumentationDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { data: dashboardDetails, isFetching: isLoading } = useQuery({
    queryKey: [GET_BI_DASHBOARD_BY_ID, dashboardRk],
    enabled: dashboardRk !== undefined && isOpen,
    queryFn: () => getBiDashboardById(dashboardRk!),
  });

  const isLoadingOrNoDashboard = isLoading || !dashboardDetails;
  const isDashboardReady = !isLoadingOrNoDashboard;
  return (
    <Dialog onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="!al-max-w-3xl !al-min-h-60">
        {isDashboardReady && (
          <DialogHeader>
            <DialogTitle>Dashboard Level Cost Attribution is not enabled</DialogTitle>
            <DialogDescription className="!al-mt-4">
              The dashboard <strong className="al-font-semibold">{dashboardName}</strong> does not
              have{" "}
              <a
                href="https://help.tableau.com/current/api/rest_api/en-us/REST/rest_api_how_to_query_tagging.htm"
                target="_blank"
                rel="noopener noreferrer"
                className="al-text-blue-600 al-underline hover:al-text-blue-800"
              >
                Query Tagging
              </a>{" "}
              enabled. Query tagging is required to accurately attribute costs from dashboard
              refreshes.
              <br />
              To start tagging queries, follow the instructions below and add the following
              statement to the{" "}
              <a
                href="https://help.tableau.com/current/pro/desktop/en-us/connect_basic_initialsql.htm"
                target="_blank"
                rel="noopener noreferrer"
                className="al-text-blue-600 al-underline hover:al-text-blue-800"
              >
                Initial SQL
              </a>{" "}
              of your tableau datasource connection -
            </DialogDescription>
          </DialogHeader>
        )}
        {isLoadingOrNoDashboard && (
          <RelativeComponentLoader label="...Loading" componentHeight={8} />
        )}
        {isDashboardReady && (
          <div className="al-space-y-4 al-mt-4">
            {instrumentationSnippet && (
              <div className="al-p-4 al-bg-gray-50 al-rounded-lg">
                <Stack justify="between" align="center" className="al-mb-2">
                  <h3 className="al-font-semibold al-text-gray-900">SQL Query Tag Configuration</h3>
                  <CopyButton value={instrumentationSnippet} />
                </Stack>
                <div className="al-rounded al-overflow-x-auto">
                  <Markdown content={`\`\`\`sql\n${instrumentationSnippet}\n\`\`\``} />
                </div>
              </div>
            )}

            <div className="al-p-4 al-bg-blue-50 al-rounded-lg">
              <h3 className="al-font-semibold al-text-blue-900 al-mb-2">Setup Instructions -</h3>
              <ol className="al-list-decimal al-list-inside al-text-blue-800 al-space-y-2">
                <li>
                  Copy the ALTER statement provided at the dashboard level using the copy button
                </li>
                <li>
                  Open your dashboard -{" "}
                  {dashboardDetails.dashboard_url && dashboardName ? (
                    <a
                      href={dashboardDetails.dashboard_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="al-text-blue-600 al-underline hover:al-text-blue-800"
                    >
                      {dashboardName}
                    </a>
                  ) : (
                    dashboardName || "Dashboard"
                  )}
                </li>
                <li>
                  In the Data dropdown, hover over the datasource and click &quot;Edit Data
                  Source&quot;
                </li>
                <li>Click on &quot;Edit Initial SQL&quot;</li>
                <li>Enter the copied snippet and save</li>
                <li>Cost attribution data will start appearing within 24-48 hours</li>
              </ol>
            </div>

            <div className="al-p-4 al-bg-green-50 al-rounded-lg">
              <h3 className="al-font-semibold al-text-green-900 al-mb-2">Documentation</h3>
              <p className="al-text-green-800">
                For detailed setup instructions, visit our documentation for{" "}
                <a
                  href="https://docs.altimate.ai/docs/tableau-insights-setup-tableau-server"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="al-text-green-600 al-underline hover:al-text-green-800"
                >
                  Tableau Server
                </a>
                {" "}or{" "}
                <a
                  href="https://docs.altimate.ai/docs/tableau-insights-setup-tableau-cloud"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="al-text-green-600 al-underline hover:al-text-green-800"
                >
                  Tableau Cloud
                </a>
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InstrumentationDialog;
